export const TRANSCRIPT_HISTORY_LIMIT = 50;

export const SEEK_STEP_SECONDS = 10;

export const MAX_AVAILABILITY_POLL_ATTEMPTS = 2;

export const AVAILABILITY_POLL_INTERVAL_MS = 5000;
export const AVAILABILITY_REQUEST_TIMEOUT_MS = 5000;

// NOTE: A video reported as "processing" may be minutes or hours away, far
// longer than the standard two-attempt availability window above, so both
// players poll with exponential backoff (initial → max interval) up to a
// total-elapsed cap instead. That picks the video up as soon as it lands
// without hammering the API, and audio keeps playing throughout. Two
// server-side windows the cap must OUTLIVE, or the player flips to
// "not available" while the work is still healthily running:
//   * the on-demand iOS MP4 transcode (MP4_TRANSCODE_STALE_MS, 15 min);
//   * the deferred playback-WebM encode of an uploaded video (NSAI-4859),
//     which runs after the meeting is already `processed` and is trusted for
//     WEBM_DEFERRED_ENCODE_STALE_MS (2 h) while its {base}.webm.pending marker
//     is fresh — the workflow itself allows the encode 1.5 h plus the upload of
//     a multi-GB result.
// Both live in server/src/vstr-video/vstr-video.constant.ts. The cap sits just
// above the longer of the two, so the terminal answer comes from the server
// ("unavailable" once the marker goes stale) rather than from the client
// giving up early.
export const VIDEO_PROCESSING_POLL_INITIAL_MS = 5000;
export const VIDEO_PROCESSING_POLL_MAX_MS = 30000;
export const VIDEO_PROCESSING_POLL_MAX_ELAPSED_MS = 125 * 60 * 1000;

export const CIRCULAR_PROGRESS_RADIUS = 13;

// NOTE: Circumference of a circle = 2 * π * radius.
// Used to calculate strokeDasharray/strokeDashoffset for the SVG circular progress indicator.
export const CIRCULAR_PROGRESS_CIRCUMFERENCE =
  2 * Math.PI * CIRCULAR_PROGRESS_RADIUS;

// NOTE: Presigned stream URLs expire after 1 hour (3600s). Refresh the URL
// when the tab/app becomes visible again if more than this threshold has elapsed
// since the last fetch, preventing stale URLs after the user returns.
export const STREAM_URL_STALE_THRESHOLD_MS = 45 * 60 * 1000;

// NOTE: Periodic refresh interval for presigned URLs — re-fetch before the
// 1-hour TTL expires so playback continues uninterrupted in long sessions.
export const STREAM_URL_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

// NOTE: Re-fetch interval for the anonymous public-share viewer's stream URL.
// The public-share media is streamed through a guarded app endpoint (not a
// presigned S3 URL), which re-checks revoke/expiry on every byte request — so
// revocation already takes effect immediately regardless of this interval. The
// periodic re-fetch remains useful for two things: noticing a processing
// recording has become available, and surfacing the 2h access-token expiry as
// a 401 so the viewer is re-prompted for the password (NSAI-4973).
export const PUBLIC_SHARE_STREAM_URL_REFRESH_INTERVAL_MS = 8 * 60 * 1000;

// NOTE: Throttle for persisting the last-watched playback position to storage.
// The RAF time loop runs at ~60fps, so we only write at most once per interval
// to avoid hammering localStorage while still keeping the resume point fresh.
export const PLAYBACK_POSITION_PERSIST_INTERVAL_MS = 3000;

// NOTE: localStorage key prefix for the per-meeting last-watched position
// (final key is prefix + meetingUuid).
export const PLAYBACK_POSITION_STORAGE_PREFIX =
  "nsai:meeting-playback-position:";

// NOTE: Ignore trivially-small positions. Resuming a couple of seconds in is
// pointless, and — crucially — treating anything below this as "no position"
// stops the initial currentTime=0 reads (before a resume seek lands) from
// overwriting a real saved position with 0.
export const MIN_PERSISTED_POSITION_SECONDS = 5;

// NOTE: Drop saved positions older than this so a meeting watched long ago does
// not silently jump mid-way on a fresh visit.
export const PLAYBACK_POSITION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// NOTE: Default volume level (percentage) for audio/video playback.
export const DEFAULT_VOLUME_PERCENT = 80;

// NOTE: Playback-speed control for the recording player (NSAI-3798). Web and
// mobile share one ladder so the same rates are offered on both platforms, and
// a rate persisted on one client stays valid on the other.
export const DEFAULT_PLAYBACK_RATE = 1;

export const PLAYBACK_RATE_OPTIONS = [
  0.5, 0.75, 1, 1.25, 1.5, 1.75, 2,
] as const;

// NOTE: The chosen speed is a viewing preference rather than a property of one
// recording, so — unlike the playback position — it is stored under a single
// key and carries across meetings.
export const PLAYBACK_RATE_STORAGE_KEY = "nsai:meeting-playback-rate";

// NOTE: Download-filename rules, shared by web and mobile so a recording, its
// summary and its transcript are named identically on both platforms
// (NSAI-4143, extended for the meeting export in NSAI-3847).
//
// Longest meeting-title base kept in a download filename — leaves room for the
// extension and stays clear of OS path-length limits.
export const MAX_DOWNLOAD_FILENAME_BASE_LENGTH = 120;

// NOTE: Characters stripped from a meeting title before using it as a download
// filename — path separators, Windows-reserved characters, and control
// characters (\p{Cc}, so no literal control chars trip no-control-regex).
export const UNSAFE_FILENAME_CHARS_REGEX = /[\\/:*?"<>|]|\p{Cc}/gu;

// NOTE: Windows reserves these device basenames even with an extension
// (CON.wav is still invalid), so a title matching one falls back to the UUID.
export const WINDOWS_RESERVED_FILENAME_REGEX =
  /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i;

// NOTE: Extensions of the two text artifacts the meeting export produces
// (NSAI-3847). Markdown for the summary — it carries headings, time ranges and
// bullet structure that a reader can still read as plain text; plain text for
// the transcript, which is a flat list of timestamped lines.
export const MEETING_SUMMARY_FILE_EXTENSION = "md";
export const MEETING_TRANSCRIPT_FILE_EXTENSION = "txt";

export const MEETING_SUMMARY_MIME_TYPE = "text/markdown";
export const MEETING_TRANSCRIPT_MIME_TYPE = "text/plain";

// NOTE: "Download all" (NSAI-3847) — the recording, the summary and the
// transcript in one archive.
export const MEETING_ARCHIVE_FILE_EXTENSION = "zip";
export const MEETING_ARCHIVE_MIME_TYPE = "application/zip";

// NOTE: The recording's own extension. The audio track is always the raw WAV
// the recorder produced; the video's extension is the stream format (webm, or
// mp4 on iOS) — see constants/vstr-video.
export const MEETING_AUDIO_FILE_EXTENSION = "wav";
export const MEETING_AUDIO_MIME_TYPE = "audio/wav";
